import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../utils/axiosInstance";
import dayjs from "dayjs";


export const fetchDonationReportByBookNo = createAsyncThunk(
  "report/fetchDonationReportByBookNo",
  async ({ from, to }) => {
    if (!from || !to) {
      throw new Error("Both 'from' and 'to' book numbers must be provided.");
    }
    const response = await axiosInstance.get("api/donationReport/bookwise", {
      params: { from, to },
    });
    return response.data;
  }
);

export const fetchDonationReportByBookNoPayModeAll = createAsyncThunk(
  "report/fetchDonationReportByBookNoPayModeAll",
  async ({ from, to }) => {
    if (!from || !to) {
      throw new Error("Both 'from' and 'to' book numbers must be provided.");
    }
    const response = await axiosInstance.get("api/donationReport/bookwise-paymodeall", {
      params: { from, to },
    });
    return response.data;
  }
);

export const fetchDonationReportByBookNoPayModeWise = createAsyncThunk(
  "report/fetchDonationReportByBookNoPayModeWise",
  async ({ from, to, payMode }) => {
    if (!from || !to || !payMode) {
      throw new Error("Both 'from', 'to' book numbers and 'payMode' must be provided.");
    }
    const response = await axiosInstance.get("api/donationReport/bookwise-paymodewise", {
      params: { from, to, payMode },
    });
    return response.data;
  }
);

export const fetchDonationReportByPayPurpose = createAsyncThunk(
  "report/fetchDonationReportByPayPurpose",
  async (payMode) => {
    const response = await axiosInstance.get("api/donationReport/paypurpose",{
      params: { payMode },
    });
    return response.data;
  }
);



export const fetchDonationReportByDonorWise = createAsyncThunk(
  "report/fetchDonationReportByDonorWise",

  async ({fromdate, todate}) => {
    try {
      const fromDateFormatted = dayjs(fromdate).startOf("day").toISOString(); // Ensures midnight UTC
const toDateFormatted = dayjs(todate).endOf("day").toISOString(); 
      const response = await axiosInstance.get("api/donationReport/donorwise", {
params: { fromDate:fromDateFormatted, toDate: toDateFormatted },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching donation report by donor wise:", error);
      throw error;
    }
  }
);

export const fetchDonationReportByDonorWiseBookReceiptNo = createAsyncThunk(
  "report/fetchDonationReportByDonorWiseBookReceiptNo",
  async ({ fromdate, todate,bookNo, receiptNo }) => {
    if (!bookNo || !receiptNo) {
      throw new Error("Both 'bookNo' and 'receiptNo' must be provided.");
    }
    const response = await axiosInstance.get("api/donationReport/donorwise-bookreceiptno", {
      params: { fromdate, todate,bookNo, receiptNo },  
    });
    return response.data; 
  }
);

export const fetchDonationReportByDonorWiseBookNo = createAsyncThunk(
  "report/fetchDonationReportByDonorWiseBookNo",
  async ({ fromdate, todate, bookNo }) => {
    if (!bookNo) {
      throw new Error("Book number must be provided.");
    }
    const response = await axiosInstance.get("api/donationReport/donorwise-bookno", {
      params: { fromdate, todate, bookNo },
    });
    return response.data;
  }
);


const donationReportSlice = createSlice({
  name: "donationReport",
  initialState: {
    bookwiseReport: [],
    bookwisePayModeReport: [],
    overallReport: [],
    loading: false,
    error: null,
    page: 1,
    limit: 10,
  },
  reducers: {
       setPage(state, action) {
                state.page = action.payload;
            },
            setLimit(state, action) {
                state.limit = action.payload;
            },
  },
  extraReducers: (builder) => {
    builder
      // Donation Report By Book No
      .addCase(fetchDonationReportByBookNo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDonationReportByBookNo.fulfilled, (state, action) => {
        state.loading = false;
        state.donationReportByBookNo = action.payload;
      })
      .addCase(fetchDonationReportByBookNo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Donation Report By Book No Pay Mode All
      .addCase(fetchDonationReportByBookNoPayModeAll.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDonationReportByBookNoPayModeAll.fulfilled, (state, action) => {
        state.loading = false;
        state.donationReportByBookNoPayModeAll = action.payload;  
      })
      .addCase(fetchDonationReportByBookNoPayModeAll.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message; 
      })
      // Donation Report By Book No Pay Mode Wise
      .addCase(fetchDonationReportByBookNoPayModeWise.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDonationReportByBookNoPayModeWise.fulfilled, (state, action) => {
        state.loading = false;
        state.donationReportByBookNoPayModeWise = action.payload;  
      })
      .addCase(fetchDonationReportByBookNoPayModeWise.rejected, (state, action) => {
        state.loading = false;  
        state.error = action.error.message;
      })
      // Donation Report By Pay Purpose
      .addCase(fetchDonationReportByPayPurpose.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDonationReportByPayPurpose.fulfilled, (state, action) => {
        state.loading = false;
        state.donationReportByPayPurpose = action.payload;
      })
      .addCase(fetchDonationReportByPayPurpose.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Donation Report By Donor Wise
      .addCase(fetchDonationReportByDonorWise.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDonationReportByDonorWise.fulfilled, (state, action) => {
        state.loading = false;
        state.donationReportByDonorWise = action.payload;
      })
      .addCase(fetchDonationReportByDonorWise.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Donation Report By Donor Wise Book Receipt No
      .addCase(fetchDonationReportByDonorWiseBookReceiptNo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDonationReportByDonorWiseBookReceiptNo.fulfilled, (state, action) => {
        state.loading = false;
        state.donationReportByDonorWiseBookReceiptNo = action.payload;
      })
      .addCase(fetchDonationReportByDonorWiseBookReceiptNo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Donation Report By Donor Wise Book No  
      .addCase(fetchDonationReportByDonorWiseBookNo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDonationReportByDonorWiseBookNo.fulfilled, (state, action) => {
        state.loading = false;
        state.donationReportByDonorWiseBookNo = action.payload;
      })
      .addCase(fetchDonationReportByDonorWiseBookNo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { setPage, setLimit } = donationReportSlice.actions;
export default donationReportSlice.reducer;
